/**
 * @fileoverview RepeatableList widget — N rows of nested fields.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render an ordered list of row objects; each row renders the nested
 *   `extras.itemTemplate.fields[]` via the dispatcher (`FieldRenderer`).
 * - Enforce `extras.minInstances` (completion gate, surfaced via wrapper
 *   `errorMessage`) and `extras.maxInstances` (disable add button).
 * - G-025 — mobile-pwa lacked this widget entirely.
 *
 * Notes:
 * - Per the per-widget spec §9 open question, nested-field transitions are
 *   collapsed into the parent list's transitions (not per-row history). The
 *   collapsed-snapshot decision belongs to Phase 5 adapter; this component
 *   simply emits the whole array on any nested change.
 *
 * @component RepeatableListField
 *
 * @contract RepeatableListFieldProps
 * Purpose: prop contract for the N-row list widget; `renderNested` is the
 * recursive dispatcher injected by `<FieldRenderer>`.
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import type { InspectionFormField } from '@firesquirrel/core-domain';

type Row = Record<string, unknown>;

export interface RepeatableListFieldProps extends BaseWidgetProps<Row[] | null | undefined> {
  /** Renderer for nested fields. Phase 3 wires this from `<FieldRenderer>` recursively. */
  renderNested?: (field: InspectionFormField, value: unknown, onChange: (next: unknown) => void) => React.ReactNode;
}

export function RepeatableListField(props: RepeatableListFieldProps) {
  const { field, value, isNA, resolved, onChange, onToggleNA, bulkPilotMode, errorMessage, requiredUnmet, renderNested } = props;
  const extras = (field.extras ?? {}) as {
    minInstances?: number;
    maxInstances?: number;
    addLabel?: string;
    removeLabel?: string;
    itemTemplate?: { fields: InspectionFormField[] };
  };
  const rows: Row[] = value ?? [];
  const max = extras.maxInstances ?? Infinity;
  const minInst = extras.minInstances ?? 0;
  const belowMin = !isNA && minInst > 0 && rows.length < minInst
    && (rows.length > 0 || field.required || resolved?.required);
  const computedError = errorMessage
    ?? (belowMin ? `At least ${minInst} required` : undefined);
  const addLabel = extras.addLabel ?? 'Add Item';
  const removeLabel = extras.removeLabel ?? 'Remove';
  const disabledByPolicy = !!resolved?.disabled || !!bulkPilotMode;

  function addRow() {
    if (rows.length >= max) return;
    onChange([...rows, {}]);
  }
  function removeRow(idx: number) {
    onChange(rows.filter((_, i) => i !== idx));
  }
  function updateRowField(idx: number, fieldId: string, next: unknown) {
    onChange(rows.map((r, i) => i === idx ? { ...r, [fieldId]: next } : r));
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="repeatableList"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet}
    >
      <div role="list" aria-label={field.label} className="flex flex-col gap-2">
        {rows.map((row, idx) => (
          <div key={idx} role="listitem" data-row-index={idx} className="border border-gray-200 rounded-sm p-2 flex flex-col gap-2">
            {extras.itemTemplate?.fields?.map((nestedField) => (
              renderNested?.(
                nestedField,
                row[nestedField.id],
                (next: unknown) => updateRowField(idx, nestedField.id, next),
              )
            ))}
            <button
              type="button"
              onClick={() => removeRow(idx)}
              disabled={disabledByPolicy}
              aria-label={`${removeLabel} row ${idx + 1}`}
              className="self-end text-xs text-red-500 hover:text-red-700"
            >
              {removeLabel}
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          disabled={disabledByPolicy || rows.length >= max}
          className="px-3 py-2 text-sm border border-dashed border-gray-300 rounded-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {addLabel} ({rows.length}{Number.isFinite(max) ? `/${max}` : ''})
        </button>
      </div>
    </FieldWrapper>
  );
}
